import React, { useState } from "react";
import DataTable from "components/common/desktopDataTable";
import { quoteSearchColumns, filterColumns } from "./utils";
import { displayDesktopRowItem } from "../common/desktopDataTable/utils";
import CustomTooltip from "common/tooltip";
import { useBoolVariation } from "@launchdarkly/react-sdk";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../store/slices/api";
import "./style.scss";

const SearchTable = () => {
  const [searchFilterValue, setSearchFilterValue] = useState("");
  const [searchParams] = useSearchParams();
  const enablePunchout = useBoolVariation("punchout", false);
  const { startDate, endDate, ...filterParams } = Object.fromEntries(searchParams.entries());
  const params = { size: "1000", sort: "updatedTimeStamp,desc", ...filterParams };
  if (startDate || endDate) {
    params.updatedTimeStamp = [`${startDate ?? "1900-01-01"} 00:00:00`, `${endDate ?? "2100-01-01"} 23:59:59`];
  }
  const { data = [], isFetching } = api.useSearchQuotesQuery(params);
  const tdClass = "table-root__body__tr__td table-root__body__tr__td__text";
  const icons = {
    sent: { color: "#2f9e44", symbol: "\u2713", label: "Sent", tooltip: "This quote has been sent to PunchOut" },
    eligible: {
      color: "#1c7ed6",
      symbol: "\u25cf",
      label: "Eligible",
      tooltip: "This account is eligible for PunchOut. Open the quote and select 'Add to PunchOut' to publish it.",
    },
    ineligible: {
      color: "#e03131",
      symbol: "\u2715",
      label: "Ineligible",
      tooltip: "This account is not eligible for PunchOut Orders",
    },
  };

  const getPunchoutStatus = (row, catalogTypes) => {
    if (row.punchoutPublished === true) return "sent";
    const catalogType = catalogTypes[row.accountNumber];
    return catalogType === "both" || catalogType === "punchout" ? "eligible" : "ineligible";
  };

  const accountNumbers = [
    ...new Set(
      data
        .map((row) => row.accountNumber)
        .filter(Boolean)
        .map(String),
    ),
  ];
  const { data: accounts = [] } = api.useGetAccountsQuery(
    { page: 0, size: accountNumbers.length, customerNumber: accountNumbers },
    { skip: !enablePunchout || !accountNumbers.length },
  );

  const catalogTypes = Object.fromEntries(
    accounts.map(({ customerNumber, fcCatalogType }) => [String(customerNumber), fcCatalogType ?? null]),
  );

  const filteredTableList = data.filter((row) => {
    const columnsTemp = filterColumns;
    return columnsTemp.some((column) => {
      const variable = row[column] == null ? "" : row[column].toString();
      return (
        variable
          .replace(/[^a-zA-Z0-9]/g, "")
          .toLowerCase()
          .indexOf(searchFilterValue.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()) > -1
      );
    });
  });

  const handleSearchFilter = (e) => {
    const value = e.target.value;
    setSearchFilterValue(value);
  };
  const renderPriceQuoteRows = (currentData) => {
    return currentData.map((row) => {
      const punchoutStatus = getPunchoutStatus(row, catalogTypes);
      return (
        <tr key={row.quoteId}>
          {quoteSearchColumns.map((col, index) => {
            return (
              <React.Fragment key={`${row.quoteId}-${index}`}>
                {col.name === "Quote Name" && (
                  <td key={`${row.quoteId}-${index}`}>
                    <CustomTooltip placement="top" trigger="hover" text={row[col.dataKey]}>
                      <Link
                        to={`/priceQuote/${row.quoteId}#quote-summary`}
                        className="mobile-data__row__column__item__top__left__name--text customer-txt"
                      >
                        <span>{displayDesktopRowItem(row, col)}</span>
                      </Link>
                    </CustomTooltip>
                  </td>
                )}
                {col.name === "Updated Date" && (
                  <td className={tdClass + " price-quote-search"} key={`${row.quoteId}-${index}`}>
                    {row[col.dataKey] ? new Date(row[col.dataKey]).toLocaleDateString("en-US") : ""}
                  </td>
                )}
                {col.name !== "Quote Name" && col.name !== "Updated Date" && col.name !== "PunchOut" && (
                  <td
                    className={tdClass + " price-quote-search" + (col.name === "Account Name" ? " account-nbr" : "")}
                    key={`${row.quoteId}-${index}`}
                  >
                    {displayDesktopRowItem(row, col)}
                  </td>
                )}
                {col.name === "PunchOut" && enablePunchout && (
                  <td className="ps-2 pe-3" key={`${row.quoteId}-${index}-punchout`}>
                    <CustomTooltip placement="top" trigger="hover" text={icons[punchoutStatus].tooltip}>
                      <>
                        <span style={{ color: "#000" }}>{icons[punchoutStatus].label}</span>{" "}
                        <span style={{ color: icons[punchoutStatus].color }}>{icons[punchoutStatus].symbol}</span>
                      </>
                    </CustomTooltip>
                  </td>
                )}
              </React.Fragment>
            );
          })}
        </tr>
      );
    });
  };

  return (
    <DataTable
      key={data.length}
      tableHeader={quoteSearchColumns.filter((column) => enablePunchout || column.name !== "PunchOut")}
      tableBody={data}
      renderDesktopRows={renderPriceQuoteRows}
      countTitle="Search Results"
      displaySearchFilter={true}
      filteredTableList={filteredTableList}
      searchFilterValue={searchFilterValue}
      handleSearchFilter={handleSearchFilter}
      isLoading={isFetching}
    />
  );
};

export default SearchTable;
